import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  Plus,
  Trash2,
  Download,
  ChevronDown,
  ChevronUp,
  Upload,
  X,
  Pencil,
  Save,
} from 'lucide-react';
import api from '../../lib/api';
import { cn } from '../../lib/cn';
import { money } from '../../lib/money';
import type {
  FirmSettings,
  CaseLiability,
  AssetFull,
  IAReadinessSection,
  SwornInventoryMeta,
  AssetSupplemental,
} from '../../types';
import {
  useIAReadiness,
  useIAPreview,
  useSwornInventories,
  useGenerateSwornInventory,
  useDeleteSwornInventory,
  useLiabilities,
  useCreateLiability,
  useUpdateLiability,
  useDeleteLiability,
  useAssetsWithSupplemental,
  useUpdateAssetSupplemental,
  useInventoryDate,
  useSetInventoryDate,
  useIaChecklist,
  useUpdateIaChecklist,
  type IaChecklistCategory,
  type IAPreview,
} from '../../hooks/useInventoryAppraisement';
import {
  Button,
  IconButton,
  Input,
  Select,
  TextArea,
  Card,
  CardTitle,
  Tag,
  Money,
  Skeleton,
  EmptyState,
  Tabs,
  PageHeader,
  BUTTON_BASE,
  BUTTON_VARIANTS,
  type TabItem,
  type SelectOption,
} from '../ui';

const fmt = (v: number | null | undefined) => money(v ?? 0);

const SP_BASIS_OPTIONS: SelectOption[] = [
  { value: 'pre_marital', label: 'Pre-marital property' },
  { value: 'gift', label: 'Gift' },
  { value: 'inheritance', label: 'Inheritance' },
  { value: 'pi_recovery', label: 'Personal injury recovery' },
  { value: 'other', label: 'Other basis' },
];

const LIABILITY_TYPES = [
  'Mortgage',
  'HELOC',
  'Auto Loan',
  'Credit Card',
  'Student Loan',
  'Business Loan',
  'Promissory Note',
  'Medical Debt',
  'Other',
];

const LIABILITY_TYPE_OPTIONS: SelectOption[] = LIABILITY_TYPES.map(t => ({ value: t, label: t }));

const PROPERTY_STATUS_OPTIONS: SelectOption[] = [
  { value: 'CP_candidate', label: 'Community' },
  { value: 'SP_candidate', label: 'Separate' },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === 'complete') {
    return <Tag tone="good" icon={CheckCircle2}>Complete</Tag>;
  }
  if (status === 'partial') {
    return <Tag tone="warn" icon={AlertCircle}>Partial</Tag>;
  }
  return <Tag tone="danger" icon={Clock}>Missing</Tag>;
}

// ─── Readiness Card ───────────────────────────────────────────────────────────

function ReadinessCard({ section }: { section: IAReadinessSection }) {
  const hasIssues = (section.issues?.length ?? 0) > 0 || (section.missing_fields?.length ?? 0) > 0;
  const [expanded, setExpanded] = useState(hasIssues && section.status !== 'complete');

  return (
    <Card padding="compact">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-ink">{section.name}</p>
          {section.asset_count !== undefined && (
            <p className="text-xs text-ink-soft mt-1">
              {section.asset_count} asset{section.asset_count !== 1 ? 's' : ''}
              {section.total_value !== undefined ? ` · ${fmt(section.total_value)}` : ''}
            </p>
          )}
          {section.filled !== undefined && section.total !== undefined && (
            <p className="text-xs text-ink-soft mt-1">
              {section.filled}/{section.total} fields complete
            </p>
          )}
          {section.account_count !== undefined && (
            <p className="text-xs text-ink-soft mt-1">
              {section.account_count} account{section.account_count !== 1 ? 's' : ''} uploaded
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={section.status} />
          {hasIssues && (
            <IconButton
              size="xs"
              icon={expanded ? ChevronUp : ChevronDown}
              aria-label={expanded ? 'Collapse issues' : 'Expand issues'}
              onClick={() => setExpanded(v => !v)}
              className="text-ink-faint hover:text-ink-soft"
            />
          )}
        </div>
      </div>

      {expanded && hasIssues && (
        <div className="mt-2 pt-2 border-t border-border-subtle">
          {(section.missing_fields ?? []).map(f => (
            <p key={f} className="text-xs text-warn">• {f} missing</p>
          ))}
          {(section.issues ?? []).map((iss, i) => (
            <p key={i} className="text-xs text-warn">• {iss}</p>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Liability Row ────────────────────────────────────────────────────────────

function LiabilityRow({
  liability,
  onUpdate,
  onDelete,
}: {
  liability: CaseLiability;
  onUpdate: (updates: Partial<CaseLiability>) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...liability });

  const save = () => {
    onUpdate({
      creditor_name: form.creditor_name,
      account_last_four: form.account_last_four,
      balance: form.balance,
      property_status: form.property_status,
      assigned_to: form.assigned_to,
      liability_type: form.liability_type,
      notes: form.notes,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="h-11 bg-clay-50">
        <td className="px-3 py-2">
          <Input
            size="sm"
            value={form.creditor_name}
            onChange={e => setForm(f => ({ ...f, creditor_name: e.target.value }))}
          />
        </td>
        <td className="px-3 py-2">
          <Input
            size="sm"
            maxLength={4}
            placeholder="4321"
            value={form.account_last_four ?? ''}
            onChange={e => setForm(f => ({ ...f, account_last_four: e.target.value || null }))}
          />
        </td>
        <td className="px-3 py-2">
          <Select
            size="sm"
            placeholder="— Type —"
            options={LIABILITY_TYPE_OPTIONS}
            value={form.liability_type ?? ''}
            onChange={e => setForm(f => ({ ...f, liability_type: e.target.value || null }))}
          />
        </td>
        <td className="px-3 py-2">
          <Input
            size="sm"
            type="number"
            value={form.balance ?? ''}
            onChange={e => setForm(f => ({ ...f, balance: e.target.value ? Number(e.target.value) : null }))}
          />
        </td>
        <td className="px-3 py-2">
          <Select
            size="sm"
            options={PROPERTY_STATUS_OPTIONS}
            value={form.property_status ?? 'CP_candidate'}
            onChange={e => setForm(f => ({ ...f, property_status: e.target.value }))}
          />
        </td>
        <td className="px-3 py-2">
          <Input
            size="sm"
            value={form.assigned_to ?? ''}
            onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value || null }))}
            placeholder="Client / Opposing"
          />
        </td>
        <td className="px-3 py-2">
          <Input
            size="sm"
            value={form.notes ?? ''}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value || null }))}
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex gap-1">
            <IconButton
              size="xs"
              icon={Save}
              aria-label="Save"
              onClick={save}
              className="text-good"
            />
            <IconButton
              size="xs"
              icon={X}
              aria-label="Cancel"
              onClick={() => setEditing(false)}
              className="text-ink-faint hover:text-ink-soft"
            />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="h-11 hover:bg-cream">
      <td className="px-3 py-2 text-sm font-medium text-ink">{liability.creditor_name}</td>
      <td className="px-3 py-2 text-sm text-ink-soft">{liability.account_last_four ? `···${liability.account_last_four}` : '—'}</td>
      <td className="px-3 py-2 text-sm text-ink-soft">{liability.liability_type ?? '—'}</td>
      <td className="px-3 py-2 text-right"><Money value={liability.balance ?? 0} /></td>
      <td className="px-3 py-2 text-sm">
        <Tag tone={(liability.property_status ?? '').includes('SP') ? 'separate' : 'community'}>
          {(liability.property_status ?? '').includes('SP') ? 'Separate' : 'Community'}
        </Tag>
      </td>
      <td className="px-3 py-2 text-sm text-ink-soft">{liability.assigned_to ?? '—'}</td>
      <td className="px-3 py-2 text-sm text-ink-soft max-w-xs truncate">{liability.notes ?? '—'}</td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <IconButton
            size="xs"
            icon={Pencil}
            aria-label="Edit"
            onClick={() => setEditing(true)}
            className="text-ink-faint hover:text-ink-soft"
          />
          <IconButton
            size="xs"
            icon={Trash2}
            aria-label="Delete"
            variant="danger-ghost"
            onClick={onDelete}
          />
        </div>
      </td>
    </tr>
  );
}

// ─── Asset SP Fields Editor ───────────────────────────────────────────────────

function AssetSPEditor({
  asset,
  onUpdate,
}: {
  asset: AssetFull;
  onUpdate: (updates: Partial<AssetSupplemental>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<AssetSupplemental>>({
    sp_basis: asset.sp_basis,
    sp_basis_notes: asset.sp_basis_notes,
    legal_description: asset.legal_description,
    vehicle_vin: asset.vehicle_vin,
    vehicle_year: asset.vehicle_year,
    vehicle_make: asset.vehicle_make,
    vehicle_model: asset.vehicle_model,
    retirement_plan_type: asset.retirement_plan_type,
    retirement_employer: asset.retirement_employer,
    insurance_insurer: asset.insurance_insurer,
    insurance_policy_number: asset.insurance_policy_number,
  });

  const isSP = (asset.property_status ?? '').toLowerCase().includes('sp');

  const save = () => {
    onUpdate(form);
    setOpen(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <p className="text-sm font-medium text-ink">{asset.name}</p>
            <p className="text-xs text-ink-soft">{asset.asset_type} · {fmt(asset.estimated_value)}</p>
          </div>
          {(() => {
            const needs: string[] = [];
            if (isSP && !asset.sp_basis) needs.push('SP basis');
            if (asset.asset_type === 'Vehicle' && !asset.vehicle_vin) needs.push('VIN');
            if (asset.asset_type === 'Real Estate' && !asset.legal_description) needs.push('legal description');
            if (asset.asset_type === 'Retirement Account' && !asset.retirement_plan_type) needs.push('plan type');
            if (asset.asset_type === 'Life Insurance' && !asset.insurance_policy_number) needs.push('policy #');
            return needs.length === 0 ? (
              <Tag size="sm" tone="good" icon={CheckCircle2}>Complete</Tag>
            ) : (
              <Tag size="sm" tone="warn" icon={AlertCircle} title={`Needs: ${needs.join(', ')}`}>
                Needs detail
              </Tag>
            );
          })()}
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={Pencil}
          iconTrailing={open ? ChevronUp : ChevronDown}
          onClick={() => setOpen(o => !o)}
        >
          Edit Details
        </Button>
      </div>

      {open && (
        <div className="mt-3 p-3 bg-cream rounded-md border border-border space-y-3">
          {isSP && (
            <>
              <Select
                label="Separate Property Basis"
                placeholder="— Select basis —"
                options={SP_BASIS_OPTIONS}
                value={form.sp_basis ?? ''}
                onChange={e => setForm(f => ({ ...f, sp_basis: e.target.value || null }))}
              />
              <Input
                label="Basis Notes"
                placeholder="e.g. Inherited from grandfather's estate per will dated..."
                value={form.sp_basis_notes ?? ''}
                onChange={e => setForm(f => ({ ...f, sp_basis_notes: e.target.value || null }))}
              />
            </>
          )}

          {asset.asset_type === 'Real Estate' && (
            <TextArea
              label="Legal Description"
              rows={2}
              placeholder="Lot 12, Block 3, Riverwood Subdivision..."
              value={form.legal_description ?? ''}
              onChange={e => setForm(f => ({ ...f, legal_description: e.target.value || null }))}
            />
          )}

          {asset.asset_type === 'Vehicle' && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Year"
                type="number"
                placeholder="2021"
                value={form.vehicle_year ?? ''}
                onChange={e => setForm(f => ({ ...f, vehicle_year: e.target.value ? Number(e.target.value) : null }))}
              />
              <Input
                label="Make"
                placeholder="Toyota"
                value={form.vehicle_make ?? ''}
                onChange={e => setForm(f => ({ ...f, vehicle_make: e.target.value || null }))}
              />
              <Input
                label="Model"
                placeholder="Camry"
                value={form.vehicle_model ?? ''}
                onChange={e => setForm(f => ({ ...f, vehicle_model: e.target.value || null }))}
              />
              <Input
                label="VIN"
                placeholder="1HGCM82633A004352"
                value={form.vehicle_vin ?? ''}
                onChange={e => setForm(f => ({ ...f, vehicle_vin: e.target.value || null }))}
              />
            </div>
          )}

          {asset.asset_type === 'Retirement Account' && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Plan Type"
                placeholder="401(k), IRA, 403(b)..."
                value={form.retirement_plan_type ?? ''}
                onChange={e => setForm(f => ({ ...f, retirement_plan_type: e.target.value || null }))}
              />
              <Input
                label="Employer"
                placeholder="Acme Corp"
                value={form.retirement_employer ?? ''}
                onChange={e => setForm(f => ({ ...f, retirement_employer: e.target.value || null }))}
              />
            </div>
          )}

          {asset.asset_type === 'Life Insurance' && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Insurer"
                placeholder="Northwestern Mutual"
                value={form.insurance_insurer ?? ''}
                onChange={e => setForm(f => ({ ...f, insurance_insurer: e.target.value || null }))}
              />
              <Input
                label="Policy Number"
                placeholder="POL-123456"
                value={form.insurance_policy_number ?? ''}
                onChange={e => setForm(f => ({ ...f, insurance_policy_number: e.target.value || null }))}
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" size="lg" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="lg" onClick={save}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Checklist ────────────────────────────────────────────────────────────────

function ChecklistView({ caseId }: { caseId: string }) {
  const { data, isLoading } = useIaChecklist(caseId);
  const update = useUpdateIaChecklist(caseId);

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton variant="rect" className="h-24 rounded-lg" />
        <Skeleton variant="rect" className="h-40 rounded-lg" />
        <Skeleton variant="rect" className="h-40 rounded-lg" />
      </div>
    );
  }
  const categories = data?.categories ?? [];

  // Group by the stable heading id (label is display-only), preserving document order.
  const groups: { group: string; label: string; cats: IaChecklistCategory[] }[] = [];
  for (const c of categories) {
    let g = groups.find(x => x.group === c.heading_group);
    if (!g) { g = { group: c.heading_group, label: c.heading_label, cats: [] }; groups.push(g); }
    g.cats.push(c);
  }

  const addressed = categories.filter(c => c.has_data || c.confirmed_none).length;
  const pending = categories.length - addressed;

  return (
    <div className="space-y-5">
      <Card>
        <CardTitle className="mb-1">Disclosure checklist</CardTitle>
        <p className="text-xs text-ink-soft">
          {addressed} of {categories.length} categories addressed.{' '}
          {pending > 0
            ? `${pending} still need an entry or a confirmed "None" before the document can swear a complete inventory.`
            : 'Every category has data or is confirmed "None".'}
        </p>
        <p className="mt-2 text-xs text-warn">
          Confirm "None" only when you have verified there are no such assets — it is a sworn statement of absence.
          Categories left unconfirmed print a "to be confirmed" placeholder, never a false "None."
        </p>
        {update.isError && (
          <p className="mt-2 text-xs text-danger">Couldn't save that change — please try again.</p>
        )}
      </Card>

      {groups.map(group => (
        <Card key={group.group} padding="none" className="overflow-hidden">
          <div className="px-4 py-2 bg-cream border-b border-border">
            <h4 className="t-eyebrow">{group.label}</h4>
          </div>
          <div className="divide-y divide-border-subtle">
            {group.cats.map(c => (
              <div key={c.key} className="flex min-h-11 items-center justify-between px-4 py-2">
                <div className="min-w-0">
                  <span className="text-sm text-ink">{c.number}. {c.title}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {c.has_data ? (
                    <Tag tone="good">
                      {c.item_count} item{c.item_count === 1 ? '' : 's'}
                    </Tag>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border-strong accent-green-700"
                        checked={c.confirmed_none}
                        onChange={e => update.mutate({ category_key: c.key, confirmed: e.target.checked })}
                      />
                      <span className={`text-xs ${c.confirmed_none ? 'text-ink font-medium' : 'text-ink-faint'}`}>
                        Confirm "None"
                      </span>
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Document Preview ─────────────────────────────────────────────────────────

function DocumentPreview({ preview }: { preview: IAPreview }) {
  const SECTION_COLORS: Record<number, string> = {
    0: 'border-char-community bg-char-community-soft',
    1: 'border-char-separate bg-char-separate-soft',
    2: 'border-char-mixed bg-char-mixed-soft',
    3: 'border-clay-200 bg-clay-50',
  };

  return (
    <div className="space-y-4">
      {/* Case Header */}
      <Card>
        <CardTitle className="mb-3">Document Header</CardTitle>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {preview.case_info.firm_name && (
            <div><span className="text-ink-faint text-xs">Firm:</span> <span className="text-ink">{preview.case_info.firm_name}</span></div>
          )}
          {preview.case_info.attorney_name && (
            <div><span className="text-ink-faint text-xs">Attorney:</span> <span className="text-ink">{preview.case_info.attorney_name}</span></div>
          )}
          {preview.case_info.client_name && (
            <div><span className="text-ink-faint text-xs">Client:</span> <span className="text-ink">{preview.case_info.client_name}</span></div>
          )}
          {preview.case_info.opposing_party_name && (
            <div><span className="text-ink-faint text-xs">Opposing Party:</span> <span className="text-ink">{preview.case_info.opposing_party_name}</span></div>
          )}
          {preview.case_info.cause_number && (
            <div><span className="text-ink-faint text-xs">Cause No.:</span> <span className="text-ink">{preview.case_info.cause_number}</span></div>
          )}
          {preview.case_info.court && (
            <div><span className="text-ink-faint text-xs">Court:</span> <span className="text-ink">{preview.case_info.court}</span></div>
          )}
          {preview.case_info.county && (
            <div><span className="text-ink-faint text-xs">County:</span> <span className="text-ink">{preview.case_info.county}</span></div>
          )}
          {preview.case_info.inventory_date && (
            <div><span className="text-ink-faint text-xs">Inventory Date:</span> <span className="text-ink">{new Date(preview.case_info.inventory_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
          )}
        </div>
      </Card>

      {/* Sections */}
      {preview.sections.map((section, idx) => (
        <div key={section.title} className={`border rounded-lg overflow-hidden ${SECTION_COLORS[idx] || 'border-border bg-cream'}`}>
          <div className="flex items-center justify-between px-4 py-2">
            <h3 className="text-sm font-semibold text-ink">{section.title}</h3>
            <Money value={section.total ?? 0} className="text-ink-soft" />
          </div>
          {section.items.length === 0 ? (
            <div className="px-4 pb-3 text-xs text-ink-faint italic">No items</div>
          ) : (
            <div className="bg-cream-light divide-y divide-border-subtle">
              {section.items.slice(0, 20).map((item, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2 gap-3">
                  <div className="min-w-0">
                    <span className="text-sm text-ink">{item.name ?? item.creditor_name ?? '—'}</span>
                    {item.asset_type && <span className="ml-2 text-xs text-ink-faint">{item.asset_type}</span>}
                    {item.liability_type && <span className="ml-2 text-xs text-ink-faint">{item.liability_type}</span>}
                    {item.source_citation && (
                      <p className="text-2xs text-ink-faint truncate" title={item.source_citation}>
                        {item.source_citation}
                      </p>
                    )}
                  </div>
                  <span className="text-sm text-ink-soft shrink-0">
                    {item.value_known === false ? 'TBD' : fmt(item.estimated_value ?? item.balance ?? 0)}
                  </span>
                </div>
              ))}
              {section.items.length > 20 && (
                <div className="px-4 py-2 text-xs text-ink-faint italic">
                  …and {section.items.length - 20} more items
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Summary */}
      <Card>
        <CardTitle className="mb-2">Summary</CardTitle>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-char-community-soft rounded-md">
            <p className="text-xs text-ink-soft mb-1">Net Community Estate</p>
            <Money value={preview.summary.net_community_estate ?? 0} whole className="t-num-hero text-char-community" />
          </div>
          <div className="text-center p-3 bg-char-separate-soft rounded-md">
            <p className="text-xs text-ink-soft mb-1">Net Separate Estate</p>
            <Money value={preview.summary.net_separate_estate ?? 0} whole className="t-num-hero text-char-separate" />
          </div>
        </div>
      </Card>

      {/* Attorney-attention warnings — incomplete data flagged, never papered over */}
      {preview.warnings && preview.warnings.length > 0 && (
        <div className="bg-warn-soft border border-warn rounded-lg p-4">
          <h3 className="text-sm font-semibold text-warn mb-2">Resolve before swearing</h3>
          <ul className="list-disc list-inside space-y-1">
            {preview.warnings.map((w, i) => (
              <li key={i} className="text-xs text-warn">{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface Props {
  caseId: string;
}

export default function InventoryAppraisementTab({ caseId }: Props) {
  const [activeSection, setActiveSection] = useState<'overview' | 'preview' | 'assets' | 'liabilities' | 'checklist' | 'history' | 'style'>('overview');
  const [generating, setGenerating] = useState(false);
  const [newLiabilityOpen, setNewLiabilityOpen] = useState(false);
  const [newLiab, setNewLiab] = useState<Partial<CaseLiability>>({ property_status: 'CP_candidate' });
  const [inventoryDateValue, setInventoryDateValue] = useState('');
  const [letterheadError, setLetterheadError] = useState<string | null>(null);
  const [iaLetterheadError, setIaLetterheadError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const extractAxiosDetail = (err: unknown): string => {
    if (
      err !== null &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: unknown }).response !== null &&
      typeof (err as { response?: unknown }).response === 'object'
    ) {
      const resp = (err as { response: { data?: unknown } }).response;
      if (resp && typeof resp.data === 'object' && resp.data !== null) {
        const detail = (resp.data as Record<string, unknown>).detail;
        if (typeof detail === 'string') return detail;
      }
    }
    return '';
  };

  const uploadLetterhead = async (file: File, endpoint: string, setError: (s: string | null) => void) => {
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      queryClient.invalidateQueries({ queryKey: ['firm-settings'] });
    } catch (err) {
      setError(extractAxiosDetail(err) || 'Upload failed. Please ensure the file is a valid .docx or .pdf under 10 MB.');
    }
  };

  const { data: readiness, isLoading: readinessLoading } = useIAReadiness(caseId);
  const { data: preview } = useIAPreview(caseId);
  const { data: inventories } = useSwornInventories(caseId);
  const { data: liabilities } = useLiabilities(caseId);
  const { data: assets } = useAssetsWithSupplemental(caseId);
  const { data: invDateData } = useInventoryDate(caseId);

  const { data: firmSettings } = useQuery<FirmSettings>({
    queryKey: ['firm-settings'],
    queryFn: async () => {
      const { data } = await api.get('/firm-settings');
      return data;
    },
  });

  const generateMutation = useGenerateSwornInventory(caseId);
  const deleteMutation = useDeleteSwornInventory(caseId);
  const createLiab = useCreateLiability(caseId);
  const updateLiab = useUpdateLiability(caseId);
  const deleteLiab = useDeleteLiability(caseId);
  const updateSupplemental = useUpdateAssetSupplemental(caseId);
  const setInventoryDate = useSetInventoryDate(caseId);

  useEffect(() => {
    if (!invDateData) return;
    const effective = invDateData.inventory_date ?? invDateData.separation_date ?? invDateData.filing_date ?? '';
    setInventoryDateValue(effective);
  }, [invDateData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateMutation.mutateAsync();
      setActiveSection('history');
    } catch (err) {
      console.error('Generation failed', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleAddLiability = async () => {
    if (!newLiab.creditor_name?.trim()) return;
    await createLiab.mutateAsync({
      creditor_name: newLiab.creditor_name!,
      account_last_four: newLiab.account_last_four ?? null,
      balance: newLiab.balance ?? null,
      property_status: newLiab.property_status ?? 'CP_candidate',
      assigned_to: newLiab.assigned_to ?? null,
      liability_type: newLiab.liability_type ?? null,
      notes: newLiab.notes ?? null,
    });
    setNewLiab({ property_status: 'CP_candidate' });
    setNewLiabilityOpen(false);
  };

  const sections: TabItem[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'assets', label: 'Asset Details' },
    { id: 'liabilities', label: 'Liabilities' },
    { id: 'checklist', label: 'Checklist' },
    { id: 'preview', label: 'Document Preview' },
    { id: 'history', label: 'History', count: inventories?.inventories?.length || undefined },
    { id: 'style', label: 'Style & Format' },
  ];

  const generateDisabledReason = !readiness
    ? 'Loading readiness…'
    : !readiness.can_generate
    ? 'Add at least one asset or upload a statement before generating an I&A.'
    : '';

  const spAssets = (assets ?? []).filter(a => (a.property_status ?? '').toLowerCase().includes('sp'));
  const allAssets = assets ?? [];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <PageHeader
        className="mb-0"
        title="Sworn Inventory & Appraisement"
        description="Texas Family Code §6.502(a)(1) — formal estate inventory for court filing"
        actions={
          <Button
            size="lg"
            icon={FileText}
            loading={generating}
            onClick={handleGenerate}
            disabled={!readiness?.can_generate}
            title={generateDisabledReason || 'Generate a Sworn I&A as .docx'}
          >
            {generating ? 'Generating…' : 'Generate I&A'}
          </Button>
        }
      />

      {/* Sub-navigation */}
      <Tabs
        items={sections}
        value={activeSection}
        onChange={id => setActiveSection(id as typeof activeSection)}
        level="secondary"
        ariaLabel="Inventory & Appraisement sections"
      />

      {/* Overview */}
      {activeSection === 'overview' && (
        <div className="space-y-4">
          {/* Texas I&A Reference Panel */}
          <Card>
            <div className="flex items-start gap-2 mb-2">
              <FileText size={16} className="text-green-900 mt-px shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-green-900">What goes in a Texas Sworn I&A</h3>
                <p className="text-xs text-ink-soft mt-1">
                  Tex. Fam. Code §6.502 — required components of a formal court-filed inventory.
                </p>
              </div>
            </div>
            <ul className="text-xs text-ink space-y-1 pl-6 list-disc">
              <li><span className="font-medium">Case caption</span> — court, county, cause number, parties, filing party role</li>
              <li><span className="font-medium">Community property</span> — real estate (with legal description), bank/brokerage/retirement accounts, vehicles (with VIN), business interests, life insurance, tangible personal property, each with fair-market value</li>
              <li><span className="font-medium">Separate property</span> — same categories, each with a stated basis (pre-marital, gift, inheritance, PI recovery)</li>
              <li><span className="font-medium">Liabilities</span> — secured debts attached to assets and unsecured debts (credit cards, loans), with creditor, balance, and party assigned</li>
              <li><span className="font-medium">Sworn verification</span> — signature under oath that the inventory is true and complete to the best of the party's knowledge</li>
            </ul>
          </Card>

          {/* Inventory Date */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <CardTitle>Inventory Date</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 max-w-xs">
                <Input
                  type="date"
                  label="Date of Inventory (defaults to separation date, then filing date)"
                  value={inventoryDateValue}
                  onChange={e => setInventoryDateValue(e.target.value)}
                  onBlur={e => setInventoryDate.mutate(e.target.value || null)}
                />
              </div>
              {invDateData?.separation_date && (
                <div className="text-xs text-ink-soft">
                  <span className="text-ink-faint">Separation date: </span>{invDateData.separation_date}
                </div>
              )}
            </div>
          </Card>

          {/* Readiness Checklist */}
          <Card>
            <CardTitle className="mb-3">Readiness Checklist</CardTitle>
            {readinessLoading ? (
              <div className="grid grid-cols-2 gap-2">
                <Skeleton variant="rect" className="h-20 rounded-lg" />
                <Skeleton variant="rect" className="h-20 rounded-lg" />
                <Skeleton variant="rect" className="h-20 rounded-lg" />
                <Skeleton variant="rect" className="h-20 rounded-lg" />
              </div>
            ) : readiness ? (
              <div className="grid grid-cols-2 gap-2">
                {readiness.sections.map(s => <ReadinessCard key={s.name} section={s} />)}
              </div>
            ) : null}
          </Card>

          {/* Generate CTA */}
          {readiness && !readiness.can_generate && (
            <div className="bg-warn-soft border border-warn rounded-lg p-3 text-sm text-warn">
              Add at least one asset or upload a statement before generating an I&A.
            </div>
          )}
        </div>
      )}

      {/* Checklist */}
      {activeSection === 'checklist' && <ChecklistView caseId={caseId} />}

      {/* Document Preview */}
      {activeSection === 'preview' && (
        <div>
          {preview ? (
            <DocumentPreview preview={preview} />
          ) : (
            <div className="space-y-4">
              <Skeleton variant="rect" className="h-32 rounded-lg" />
              <Skeleton variant="rect" className="h-32 rounded-lg" />
              <Skeleton variant="rect" className="h-32 rounded-lg" />
            </div>
          )}
        </div>
      )}

      {/* Asset Details */}
      {activeSection === 'assets' && (
        <div className="space-y-4">
          {/* SP Assets — need basis */}
          {spAssets.length > 0 && (
            <Card>
              <CardTitle className="mb-3">Separate Property — Basis & Documentation</CardTitle>
              <p className="text-xs text-ink-soft mb-3">
                Each separate property asset needs a legal basis. This information will be included in the I&A.
              </p>
              <div className="space-y-3 divide-y divide-border-subtle">
                {spAssets.map(a => (
                  <div key={a.id} className="pt-3 first:pt-0">
                    <AssetSPEditor
                      asset={a}
                      onUpdate={updates => updateSupplemental.mutate({ assetId: a.id, updates })}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* All assets — supplemental fields */}
          <Card>
            <CardTitle className="mb-3">All Assets — Supplemental Details</CardTitle>
            <p className="text-xs text-ink-soft mb-3">
              Add vehicle VINs, retirement plan types, insurance policy numbers, and real estate legal descriptions.
            </p>
            {allAssets.length === 0 ? (
              <EmptyState
                size="inline"
                title="No assets found"
                description="Add assets in the Assets tab first."
              />
            ) : (
              <div className="space-y-3 divide-y divide-border-subtle">
                {allAssets.map(a => (
                  <div key={a.id} className="pt-3 first:pt-0">
                    <AssetSPEditor
                      asset={a}
                      onUpdate={updates => updateSupplemental.mutate({ assetId: a.id, updates })}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Liabilities */}
      {activeSection === 'liabilities' && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div>
                <CardTitle>Standalone Liabilities</CardTitle>
                <p className="text-xs text-ink-soft">
                  Debts not tied to a specific asset. Asset-linked debts (mortgages, auto loans) are pulled from the Assets tab automatically.
                </p>
              </div>
              <Button size="md" icon={Plus} onClick={() => setNewLiabilityOpen(o => !o)}>
                Add Liability
              </Button>
            </div>

            {/* New Liability Form */}
            {newLiabilityOpen && (
              <div className="mb-4 p-3 bg-cream rounded-md border border-border grid grid-cols-2 gap-3">
                <Input
                  label="Creditor / Lender"
                  required
                  placeholder="Chase Visa"
                  value={newLiab.creditor_name ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, creditor_name: e.target.value }))}
                />
                <Input
                  label="Acct Last 4"
                  placeholder="4321"
                  maxLength={4}
                  value={newLiab.account_last_four ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, account_last_four: e.target.value || null }))}
                />
                <Select
                  label="Type"
                  placeholder="— Select type —"
                  options={LIABILITY_TYPE_OPTIONS}
                  value={newLiab.liability_type ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, liability_type: e.target.value || null }))}
                />
                <Input
                  label="Balance ($)"
                  type="number"
                  placeholder="12500.00"
                  value={newLiab.balance ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, balance: e.target.value ? Number(e.target.value) : null }))}
                />
                <Select
                  label="Property Status"
                  options={PROPERTY_STATUS_OPTIONS}
                  value={newLiab.property_status ?? 'CP_candidate'}
                  onChange={e => setNewLiab(f => ({ ...f, property_status: e.target.value }))}
                />
                <Input
                  label="Assigned To"
                  placeholder="Client or Opposing"
                  value={newLiab.assigned_to ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, assigned_to: e.target.value || null }))}
                />
                <Input
                  label="Notes"
                  value={newLiab.notes ?? ''}
                  onChange={e => setNewLiab(f => ({ ...f, notes: e.target.value || null }))}
                />
                <div className="col-span-2 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => { setNewLiabilityOpen(false); setNewLiab({ property_status: 'CP_candidate' }); }}
                  >
                    Cancel
                  </Button>
                  <Button size="lg" onClick={handleAddLiability}>
                    Add
                  </Button>
                </div>
              </div>
            )}

            {/* Table */}
            {!liabilities || liabilities.length === 0 ? (
              <EmptyState size="inline" title="No standalone liabilities added yet" />
            ) : (
              <div className="overflow-x-auto">
                {/* eslint-disable-next-line no-restricted-syntax -- inline row-editing ledger: restyled in place, DataTable cannot host row editors */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Creditor</th>
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Acct #</th>
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Type</th>
                      <th scope="col" className="px-3 py-2 text-right t-eyebrow">Balance</th>
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Status</th>
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Assigned To</th>
                      <th scope="col" className="px-3 py-2 text-left t-eyebrow">Notes</th>
                      <th scope="col" className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {liabilities.map(l => (
                      <LiabilityRow
                        key={l.id}
                        liability={l}
                        onUpdate={updates => updateLiab.mutate({ id: l.id, updates })}
                        onDelete={() => deleteLiab.mutate(l.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Style & Format */}
      {activeSection === 'style' && (
        <div className="space-y-4">
          <Card>
            <CardTitle className="mb-1">Letterhead Template</CardTitle>
            <p className="text-xs text-ink-soft mb-3">
              This letterhead (.docx or .pdf) is used as the header for I&A documents and is shared with Demand Letters. PDFs are embedded as a header image on every page. Upload or replace it here.
            </p>
            {firmSettings?.has_template ? (
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 text-sm text-good">
                  <CheckCircle2 size={16} /> {firmSettings.template_filename ?? 'Letterhead uploaded'}
                </div>
                <label className="flex items-center gap-1 text-xs text-green-700 hover:underline cursor-pointer">
                  <Upload size={12} /> Replace
                  {/* eslint-disable-next-line no-restricted-syntax -- native file input inside the upload label: no ui primitive exists yet */}
                  <input
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      await uploadLetterhead(file, '/firm-settings/template', setLetterheadError);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            ) : (
              <div>
                <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-border rounded-lg text-sm text-ink-soft hover:border-green-700 hover:text-green-700 cursor-pointer transition-colors w-fit">
                  <Upload size={16} /> Upload letterhead (.docx or .pdf)
                  {/* eslint-disable-next-line no-restricted-syntax -- native file input inside the upload label: no ui primitive exists yet */}
                  <input
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      await uploadLetterhead(file, '/firm-settings/template', setLetterheadError);
                      e.target.value = '';
                    }}
                  />
                </label>
                <p className="text-xs text-warn mt-1 flex items-center gap-1">
                  <AlertCircle size={12} /> No letterhead uploaded — I&A will use plain formatting
                </p>
              </div>
            )}
            {letterheadError && (
              <p className="text-xs text-danger mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {letterheadError}
              </p>
            )}
          </Card>

          <Card>
            <CardTitle className="mb-1">I&A-Specific Letterhead</CardTitle>
            <p className="text-xs text-ink-soft mb-3">
              Optionally upload a letterhead (.docx or .pdf) used only for I&A documents (overrides the shared letterhead above for I&As only). PDFs are embedded as a header image on every page.
            </p>
            {firmSettings?.has_ia_letterhead ? (
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 text-sm text-good">
                  <CheckCircle2 size={16} /> {firmSettings.ia_letterhead_filename ?? 'I&A letterhead uploaded'}
                </div>
                <label className="flex items-center gap-1 text-xs text-green-700 hover:underline cursor-pointer">
                  <Upload size={12} /> Replace
                  {/* eslint-disable-next-line no-restricted-syntax -- native file input inside the upload label: no ui primitive exists yet */}
                  <input
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      await uploadLetterhead(file, '/firm-settings/ia-letterhead', setIaLetterheadError);
                      e.target.value = '';
                    }}
                  />
                </label>
                <Button
                  variant="danger-ghost"
                  size="sm"
                  icon={Trash2}
                  onClick={async () => {
                    await api.delete('/firm-settings/ia-letterhead');
                    queryClient.invalidateQueries({ queryKey: ['firm-settings'] });
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div>
                <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-border rounded-lg text-sm text-ink-soft hover:border-green-700 hover:text-green-700 cursor-pointer transition-colors w-fit">
                  <Upload size={16} /> Upload I&A letterhead (.docx or .pdf)
                  {/* eslint-disable-next-line no-restricted-syntax -- native file input inside the upload label: no ui primitive exists yet */}
                  <input
                    type="file"
                    accept=".docx,.pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      await uploadLetterhead(file, '/firm-settings/ia-letterhead', setIaLetterheadError);
                      e.target.value = '';
                    }}
                  />
                </label>
                <p className="text-xs text-ink-faint mt-1">Optional · Max 10 MB · .docx or .pdf</p>
              </div>
            )}
            {iaLetterheadError && (
              <p className="text-xs text-danger mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {iaLetterheadError}
              </p>
            )}
          </Card>

        </div>
      )}

      {/* History */}
      {activeSection === 'history' && (
        <div className="space-y-3">
          {!inventories?.inventories || inventories.inventories.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No inventories generated yet"
              description={'Click "Generate I&A" above to create the first one.'}
            />
          ) : (
            inventories.inventories.map((inv: SwornInventoryMeta) => (
              <Card key={inv.id} padding="none" className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-ink">{inv.title ?? `Inventory #${inv.id}`}</p>
                  {inv.generated_at && (
                    <p className="text-xs text-ink-faint">
                      Generated {new Date(inv.generated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/api/cases/${caseId}/sworn-inventories/${inv.id}/download`}
                    className={cn(BUTTON_BASE, BUTTON_VARIANTS.secondary, 'h-8 gap-2 px-3 text-sm')}
                    download
                  >
                    <Download size={16} /> Download
                  </a>
                  <IconButton
                    size="sm"
                    icon={Trash2}
                    aria-label="Delete inventory"
                    variant="danger-ghost"
                    onClick={() => deleteMutation.mutate(inv.id)}
                  />
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
